import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

const USER_TOKEN_KEY = 'mmtess_user_token'; // AM_DB identity → x-am-user-authorization
const API_TOKEN_KEY = 'mmtess_api_token';   // AM (customer-scoped) → x-am-authorization
const USER_KEY = 'mmtess_user';

/** Authenticated customer profile (subset of the `users` DTO; password never present). */
export interface CustomerUser {
  _id?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  dob?: string;
  gender?: string;
  userType?: string; // always 'CUSTOMER' for storefront accounts
  walletBalance?: number;
  rewardPoints?: number;
  membership?: { isMember?: boolean; plan?: string; expiresAt?: string };
  [k: string]: unknown;
}

/** Customer Login / Register response — mints both tokens in one call (Admin Login-style). */
interface AuthData {
  token: string;          // AM_DB identity → x-am-user-authorization
  refreshToken?: string;
  amToken: string;        // AM customer-scoped → x-am-authorization
  user: CustomerUser;
}

export interface RegisterPayload {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: string;
}

/**
 * Storefront customer auth — two public custom APIs that each mint both tokens:
 *   POST {customApiBase}/customer-login    {email,password}
 *   POST {customApiBase}/customer-register {email,password,...}
 * `amToken` → x-am-authorization (customer-scoped CRUD); `token` → x-am-user-authorization
 * (identity). Both are attached by customerAuthInterceptor and persisted so a reload keeps the
 * session; on a 401/403 the interceptor clears them and routes to /login.
 */
@Injectable({ providedIn: 'root' })
export class CustomerAuthService {
  private http = inject(HttpClient);

  private _userToken = signal<string>(localStorage.getItem(USER_TOKEN_KEY) ?? '');
  private _apiToken = signal<string>(localStorage.getItem(API_TOKEN_KEY) ?? '');
  private _user = signal<CustomerUser | null>(readJson(USER_KEY));

  readonly userToken = this._userToken.asReadonly();   // x-am-user-authorization
  readonly apiToken = this._apiToken.asReadonly();      // x-am-authorization
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => !!this._apiToken());

  /** POST to an auth endpoint and apply the returned tokens — surfacing the SERVER's error
      message (e.g. "Email already registered") on a 4xx instead of the raw HttpErrorResponse. */
  private async authPost(path: string, body: unknown): Promise<void> {
    let res: unknown;
    try {
      res = await firstValueFrom(this.http.post<unknown>(`${environment.customApiBase}${path}`, body));
    } catch (err) {
      throw extractApiError(err);
    }
    this.applyAuth(unwrap<AuthData>(res));
  }

  async login(email: string, password: string): Promise<void> {
    await this.authPost('/customer-login', { email, password });
  }

  async register(payload: RegisterPayload): Promise<void> {
    await this.authPost('/customer-register', payload);
  }

  /** Sign in with a Google ID token (from Google Identity Services). */
  async loginWithGoogle(idToken: string): Promise<void> {
    await this.authPost('/google-login', { idToken });
  }

  /** Sign in with a Facebook user access token (from the FB JS SDK). */
  async loginWithFacebook(accessToken: string): Promise<void> {
    await this.authPost('/facebook-login', { accessToken });
  }

  private applyAuth(data: AuthData): void {
    if (!data?.amToken) throw new Error('Sign-in succeeded but no access token was returned.');
    this.setUserToken(data.token ?? '');
    this.setApiToken(data.amToken);
    this.setUser(data.user ?? null);
  }

  setUserToken(token: string): void {
    this._userToken.set(token);
    persist(USER_TOKEN_KEY, token);
  }

  setApiToken(token: string): void {
    this._apiToken.set(token);
    persist(API_TOKEN_KEY, token);
  }

  setUser(user: CustomerUser | null): void {
    this._user.set(user);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  }

  logout(): void {
    this._userToken.set('');
    this._apiToken.set('');
    this._user.set(null);
    localStorage.removeItem(USER_TOKEN_KEY);
    localStorage.removeItem(API_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }
}

/** Turn an HttpErrorResponse into a friendly Error using the server's envelope message
    (`{ success:false, errors:[{message}] }` or `{ message }`) rather than the raw HTTP text. */
function extractApiError(err: unknown): Error {
  if (err instanceof Error && !(err as { error?: unknown }).error) return err;
  const body = (err as { error?: unknown })?.error;
  if (body && typeof body === 'object') {
    const b = body as { errors?: { message?: string }[]; message?: string };
    const msg = b.errors?.map((e) => e?.message).filter(Boolean).join('; ') || b.message;
    if (msg) return new Error(msg);
  }
  if (typeof body === 'string' && body.trim() && !body.startsWith('<')) return new Error(body);
  const status = (err as { status?: number })?.status;
  return new Error(status === 400 ? 'Could not complete the request. Please check your details and try again.' : 'Something went wrong. Please try again.');
}

/** Unwrap the standard API Maker envelope `{ success, data, errors }`. */
function unwrap<T>(res: unknown): T {
  if (res && typeof res === 'object') {
    const r = res as { success?: boolean; data?: unknown; errors?: { message?: string }[] };
    if (r.success === false) {
      throw new Error(r.errors?.map((e) => e.message).filter(Boolean).join('; ') || 'Request failed.');
    }
    if (r.data !== undefined) return r.data as T;
  }
  return res as T;
}

function persist(key: string, value: string): void {
  if (value) localStorage.setItem(key, value);
  else localStorage.removeItem(key);
}

function readJson(key: string): CustomerUser | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as CustomerUser) : null;
  } catch {
    return null;
  }
}
